import { useCallback, useEffect } from 'react';

import Button from 'components/common/Button';
import LandingPageMedia from 'components/landing/LandingPageMedia';
import LandingPageLink from 'components/landing/LandingPageLink';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { evHeroClick, evHeroImpression } from 'events/symphony';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';

interface PromoImage {
  src: string;
  retina?: string;
  alt: string;
}

interface MelodyEditorialPromoProps {
  slotName: string;
  slotIndex: number;
  slotDetails: {
    heading?: string;
    link: string;
    brandsrc?: string;
    brandretina?: string;
    brandalt?: string;
    componentName?: string;
    gae?: string;
    copy?: string;
    subtext?: string;
    calltoaction?: string;
    bgcolor?: string;
    promos?: PromoImage[];
    textcolor?: string;
  };
  onComponentClick?: (evt: React.MouseEvent) => void;
  shouldLazyLoad?: boolean;
}

export const MelodyEditorialPromo: React.FC<MelodyEditorialPromoProps> = ({
  slotName,
  slotIndex,
  slotDetails,
  onComponentClick,
  shouldLazyLoad
}) => {
  const { heading, link, brandsrc, brandretina, brandalt, gae, copy, subtext, calltoaction, bgcolor, promos, textcolor } = slotDetails;

  const promosLength = promos?.length;

  useEffect(() => {
    track(() => [evHeroImpression, { slotDetails, slotIndex, slotName, heroCount: promosLength }]);
  }, [promosLength, slotDetails, slotIndex, slotName]);

  const onClick = useCallback(
    (evt: React.MouseEvent) => {
      onComponentClick?.(evt);
      track(() => [evHeroClick, { slotDetails, slotIndex, slotName, heroCount: promosLength }]);
      trackEvent('TE_LANDING_HERO_CLICK', gae || slotDetails.componentName);
    },
    [onComponentClick, promosLength, slotDetails, slotIndex, slotName, gae]
  );

  const linkProps = {
    'className': 'block text-center mx-auto mb-4 sm:mb-6 md:mb-16 relative box-border max-w-[1440px] [&_img]:max-w-full [&_img]:w-full',
    onClick,
    'data-eventlabel': 'melodyEditorialPromo',
    'data-slot-id': slotName,
    'data-eventvalue': gae,
    'data-slotindex': slotIndex
  };

  const commonMediaProps = {
    type: 'image',
    sources: null,
    srcset: null,
    poster: null,
    autoplay: null,
    loop: null,
    embeddedRatio: null,
    isEmbedded: false,
    tracks: null,
    isFullWidth: false,
    slotIndex
  };

  return (
    <LandingPageLink url={link} fallbackNode="div" {...linkProps}>
      <div className="flex flex-col items-center justify-between sm:flex-row sm:gap-6">
        {promos?.[0] && (
          <div className="w-full sm:w-1/3">
            <LandingPageMedia
              {...commonMediaProps}
              src={promos[0].src}
              retina={promos[0].retina}
              alt={promos[0].alt}
              shouldLazyLoad={shouldLazyLoad}
            />
          </div>
        )}
        <div
          style={{ backgroundColor: bgcolor }}
          className="flex w-full flex-col justify-center p-6 text-center sm:min-h-[312px] sm:w-1/3 sm:p-5 lg:min-h-[420px]"
        >
          {heading && (
            <h2 style={{ color: textcolor }} className="mb-6 text-xl font-semibold sm:text-2xl">
              {heading}
            </h2>
          )}
          {brandsrc && (
            <LandingPageMedia {...commonMediaProps} src={brandsrc} retina={brandretina} alt={brandalt || ''} shouldLazyLoad={shouldLazyLoad} />
          )}
          {copy && (
            <p style={{ color: textcolor }} className="mb-6 text-base font-normal sm:text-lg">
              {copy}
            </p>
          )}
          {subtext && <p className="mb-6 text-base font-semibold sm:text-lg">{subtext}</p>}
          {calltoaction && (
            <Button
              type="button"
              height="small"
              size="small"
              variant={textcolor === 'white' ? 'outlinedReversed' : 'outlined'}
              className="mx-auto w-fit"
            >
              {calltoaction}
            </Button>
          )}
        </div>
        {promos?.[1] && (
          <div className="hidden w-1/3 sm:block">
            <LandingPageMedia
              {...commonMediaProps}
              src={promos[1].src}
              retina={promos[1].retina}
              alt={promos[1].alt}
              shouldLazyLoad={shouldLazyLoad}
            />
          </div>
        )}
      </div>
    </LandingPageLink>
  );
};

export default withErrorBoundary('MelodyEditorialPromo', MelodyEditorialPromo);
